import {get, last} from 'lodash'
import {accountTypes} from '@bdswiss/common-enums'

type TauroFactorisationRules = {
  enabled?: boolean
  multiplier?: number
  leverageDivisor?: number
  drawdownTriggerPercent?: number
  tauroProfitSplitPercent?: number
  minQualifyingDeposit?: number
  maxQualifyingDeposit?: number
}

/**
 * The Tauro 10x factorisation config that applies to the viewer, or null when none does
 * (PT-5499/PT-5492).
 *
 * `viewer.tauroFactorisation` is the server's own answer and the one account creation is
 * gated on: it covers partners, who inherit the config of the campaign they own rather
 * than of a campaign they registered under (PT-6175). The registration campaign is only
 * a fallback for clients loaded before that field was queried.
 */
export const getViewerTauroFactorisation = (viewer: unknown): TauroFactorisationRules | null => {
  const campaign = get(last(get(viewer, 'registrationCampaigns') || []), 'campaign')
  const tauro = (get(viewer, 'tauroFactorisation') ?? get(campaign, 'tauroFactorisation')) as
    | TauroFactorisationRules
    | undefined
  return tauro && tauro.enabled === true ? tauro : null
}

/**
 * Whether the account being created will be a Tauro 10x factorised account, and so
 * must carry an explicit Terms & Conditions acceptance before it can be created.
 * Factorisation applies only to live MT5 Classic (MT5_STD) accounts of clients under
 * an enabled 10x campaign; `accountSubtype` is the wizard's raw selection and may be
 * empty while the account type resolves it implicitly.
 */
export const requiresTauroTermsAcceptance = (
  viewer: unknown,
  accountType?: string,
  accountSubtype?: string,
  tauroFactorisationRequested?: boolean,
): boolean => {
  if (!accountType || !accountTypes[accountType]) return false
  // The client picked the plain MT5 Classic option, so the 10x rules do not govern this
  // account and there is nothing for them to accept (PT-6510).
  if (tauroFactorisationRequested === false) return false
  const type = accountTypes[accountType]
  if (type.category !== 'forexMt5' || type.isDemo) return false
  if (accountSubtype && accountSubtype !== 'mt5Std') return false
  return getViewerTauroFactorisation(viewer) != null
}

/**
 * Removes USD Cent (USC) from the currencies offered for a Tauro 10x factorised account
 * (PT-6600). The 10x rules do not apply to cent accounts, so a factorised MT5 Classic must
 * not be opened in USC. The standard MT5 Classic option keeps USC as before.
 */
export const withoutCentCurrencyForFactorised = <T extends {key: string}>(
  currencyList: T[],
  viewer: unknown,
  accountType?: string,
  accountSubtype?: string,
  tauroFactorisationRequested?: boolean,
): T[] =>
  requiresTauroTermsAcceptance(viewer, accountType, accountSubtype, tauroFactorisationRequested)
    ? currencyList.filter((currency) => currency.key !== 'USC')
    : currencyList

/** An entry of the "Add New Account" list, as `getAllowedAccounts` builds it. */
type AllowedAccount = {
  accountType: string
  accountSubtype?: string
  category?: string
  isDemo?: boolean
  tauroFactorised?: boolean
}

/** True for the live MT5 Classic entry — the only account 10x factorisation applies to. */
const isLiveMt5ClassicEntry = (entry: AllowedAccount): boolean =>
  !entry.isDemo && entry.category === 'forexMt5' && entry.accountSubtype === 'mt5Std'

/**
 * Splits the single MT5 Classic entry into the two options the client chooses between: the
 * 10x account and the standard one (PT-6510).
 *
 * Before this, 10x was the client's fate rather than their choice — every live MT5 Classic
 * they opened was factorised, so a 10x client could not open a standard Classic at all. Both
 * options stay on the same `mt5Std` subtype and therefore share its account slots; only the
 * `tauroFactorised` marker separates them, and it decides the chip, the 10x T&C and the
 * factorisation flag sent to the server. Clients with no enabled 10x config see the list
 * unchanged.
 */
export const withTauroClassicOptions = <T extends AllowedAccount>(
  allowedAccounts: T[],
  viewer: unknown,
): T[] => {
  if (getViewerTauroFactorisation(viewer) == null) return allowedAccounts
  return allowedAccounts.flatMap((entry) =>
    isLiveMt5ClassicEntry(entry)
      ? [
          {...entry, tauroFactorised: true},
          {...entry, tauroFactorised: false},
        ]
      : [entry],
  )
}
